import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const HistoricalFiresDialog = ({
  open,
  onClose,
  fires = [],
  loading = false,
  error = null,
  eventName = ''
}) => {
  const navigate = useNavigate();

  // Convert API timeframe format to internal format for URL query param
  const apiToInternalTimeframe = (tf) => {
    if (!tf) return 'daily';
    const conversionMap = {
      '1d': 'daily',
      '1h': 'hourly',
      '4h': '4h',
      '1w': 'weekly'
    };
    return conversionMap[tf] || tf;
  };

  // Deduplicate fires by (symbol + date + customEventId) to avoid showing
  // duplicate rows when OR-gate parent events have sub-events or when the
  // API returns duplicate entries from multiple evaluation passes
  const dedupedFires = React.useMemo(() => {
    const seen = new Map();
    return fires.filter(fire => {
      // Use customEventId if available, otherwise fall back to eventId
      const eventId = fire.customEventId ?? fire.eventId ?? '';
      const key = `${fire.symbol}-${fire.date}-${apiToInternalTimeframe(fire.timeframe) || 'daily'}-${eventId}`;
      if (seen.has(key)) {
        return false;
      }
      seen.set(key, true);
      return true;
    });
  }, [fires]);

  const handleRowClick = (fire) => {
    // Navigate to chart with the event marked at that date
    const { symbol, date, timeframe } = fire;
    const normalizedSymbol = symbol.replace('/', '-');
    const dateParam = new Date(date).toISOString().split('T')[0];
    const internalTimeframe = apiToInternalTimeframe(timeframe);
    
    navigate(`/markets/${normalizedSymbol}?timeframe=${internalTimeframe}&date=${dateParam}`);
    onClose();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    if (price == null) return 'N/A';
    const num = Number(price);
    if (num >= 1000) {
      return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else if (num >= 1) {
      return `$${num.toFixed(2)}`;
    } else {
      return `$${num.toFixed(4)}`;
    }
  };

  const formatTimeframe = (tf) => {
    if (!tf) return 'Daily';
    const timeframeMap = {
      'daily': 'Daily',
      '1d': 'Daily',
      'weekly': 'Weekly',
      '1w': 'Weekly',
      '4h': '4 Hour',
      '1h': '1 Hour'
    };
    return timeframeMap[tf] || tf;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          Recent Signals: {eventName}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Last 30 days across your watchlist
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error ? (
          <Alert
            severity="error"
            sx={{
              my: 2,
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              Failed to load signals
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {error}
            </Typography>
          </Alert>
        ) : loading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Evaluating event across watchlist symbols...
            </Typography>
          </Box>
        ) : dedupedFires.length === 0 ? (
          <Alert
            severity="info"
            sx={{
              my: 2,
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              No signals in the last 30 days
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This event may be selective — that's often a good thing for quality signals. 
              Try running it on different symbols or timeframes, or adjust the event criteria.
            </Typography>
          </Alert>
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              maxHeight: 400,
              borderRadius: 1
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>
                    Symbol
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>
                    Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>
                    Price
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>
                    Timeframe
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dedupedFires.map((fire, index) => (
                  <TableRow
                    key={`${fire.symbol}-${fire.date}-${fire.timeframe || 'daily'}-${fire.customEventId ?? fire.eventId ?? index}`}
                    hover
                    onClick={() => handleRowClick(fire)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {fire.symbol}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(fire.date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {formatPrice(fire.price)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatTimeframe(fire.timeframe)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {dedupedFires.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 2, textAlign: 'center' }}
          >
            Click any row to view on chart
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HistoricalFiresDialog;
